import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { usePostingTemplates } from "@/features/ledger/api/use-posting-templates";
import type { PostingTemplateFilter } from "@/features/ledger/api/dto";
import {
  postingTemplatesColumns,
  type PostingTemplateRow,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { PostingTemplateStatus } from "@/features/shared/api/enums";

/** Traduit les filtres de colonnes DataTableClar en `PostingTemplateFilter` (§4.2). */
const toPostingTemplateFilter = (
  filters: ColumnFiltersState
): PostingTemplateFilter => {
  const filter: PostingTemplateFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "productCodeId") filter.productCodeId = value;
    if (id === "providerId") filter.providerId = value;
    if (id === "status") filter.status = value as PostingTemplateStatus;
  }
  return filter;
};

/**
 * Modèles d'imputation paginés (`POST /PostingTemplates/all`, guide §6.4) —
 * lecture authentifiée ; création / activation / archivage réservés à
 * flashpay-superadmin / flashpay-admin. La création se fait désormais sur
 * une page dédiée (`/app/ledger/posting-templates/new`) : le formulaire
 * est trop dense (header + lignes) pour tenir dans un modal confortable.
 */
export function PostingTemplatesPage() {
  const { t } = useLedgerTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toPostingTemplateFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    usePostingTemplates(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.templates.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button asChild>
            <Link to="/app/ledger/posting-templates/new">
              <Plus className="mr-2 w-4 h-4" />
              {t("ledger.templates.create")}
            </Link>
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is PostingTemplateRow =>
          Boolean(r.id)
        )}
        columnsClar={postingTemplatesColumns}
        title={t("ledger.templates.title")}
        exportFileName="posting-templates"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />
    </section>
  );
}
